import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Modal from '~/modules/components/global/modal';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import ToastService from '~/modules/services/toast-service';

const RevealBody = styled.div`
	.warning {
		display: flex;
		gap: 0.5rem;
		align-items: flex-start;
		background: ${({ theme }) => theme['midnight-darken-5']};
		border-left: 3px solid ${({ theme }) => theme['warning'] || theme['gold-base']};
		padding: 0.75rem 1rem;
		margin-bottom: 1rem;
		font-size: 0.9rem;
	}

	.secret-row {
		display: flex;
		gap: 0.5rem;
		align-items: stretch;
	}

	.secret-value {
		flex: 1 1 auto;
		font-family: monospace;
		padding: 0.6rem 0.8rem;
		background: ${({ theme }) => theme['midnight-darken-5']};
		border-radius: 0.25rem;
		word-break: break-all;
		user-select: all;
	}

	.usage-hint {
		margin-top: 1.25rem;
	}

	.usage-hint .label {
		font-size: 0.85rem;
		color: ${({ theme }) => theme['silver-base']};
		margin-bottom: 0.4rem;
	}
`;

const WebhookSecretRevealModal = ({ secret, onClose }) => {
	return (
		<Modal
			isOpen={!!secret}
			contentLabel="Webhook signing secret"
			size="lg"
			shouldCloseOnOverlayClick={false}
			onRequestClose={onClose}
		>
			<Modal.Header>
				<h3>Webhook signing secret</h3>
			</Modal.Header>
			<Modal.Body>
				<RevealBody>
					<div className="warning">
						<Icon use="warning" color="gold-base" />
						<div>
							<strong>Copy this secret now.</strong> This is the only time it will be shown.
							Use it with a Standard Webhooks library to verify every delivery.
						</div>
					</div>
					<div className="secret-row">
						<div className="secret-value" data-testid="webhook-secret-revealed-value">
							{secret}
						</div>
						<CopyToClipboard
							text={secret || ''}
							onCopy={() => ToastService.create('success', 'Signing secret copied to clipboard')}
						>
							<Button variant="secondary" data-testid="webhook-secret-copy-button">
								<Icon use="copy" color="white" /> Copy
							</Button>
						</CopyToClipboard>
					</div>
					<div className="usage-hint">
						<div className="label">
							Pass the raw request body and the <code>webhook-id</code>, <code>webhook-timestamp</code>,
							and <code>webhook-signature</code> headers to the verifier.
						</div>
					</div>
				</RevealBody>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onClose} data-testid="webhook-secret-reveal-close-button">
					Done
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

WebhookSecretRevealModal.propTypes = {
	secret: PropTypes.string,
	onClose: PropTypes.func.isRequired
};

export default WebhookSecretRevealModal;
